import { over } from '@totalsoft/change-tracking-react';
import { ControlType } from '@totalsoft/rocket-ui';
import i18next from 'i18next';
import { curry, clone } from 'ramda';
import { isCnp } from 'utils/simple-validations';

export const isNullish = value => value === undefined || value === null;

export const objToQueryString = (obj, overrides) => {
  const copy = Object.assign(obj, overrides || {});
  const searchParams = new URLSearchParams();
  Object.entries(copy).forEach(([key, value]) => {
    if (Array.isArray(value) && value.length) {
      value.forEach(v => searchParams.append(key, v));
    } else {
      if (value !== '' && value !== null && value !== undefined && typeof value !== 'object') searchParams.set(key, value);
    }
  });
  return searchParams.toString();
};

export const clean = obj => {
  Object.keys(obj).forEach(
    key => (obj[key] == null || obj[key] === undefined || (typeof obj[key] === 'number' && isNaN(obj[key]))) && delete obj[key]
  );
};

export const getInt = nr => {
  let o = parseInt(nr, 10);
  if (isNaN(o)) return null;
  return o;
};

export const getIntOrDefaultValue = (nr, defaultValue) => {
  let o = parseInt(nr, 10);
  if (isNaN(o)) return defaultValue;
  return o;
};

export const isValidNumber = input => {
  if (input === undefined || input === null) return false;
  if (isNaN(input)) return false;
  return true;
};

export const changeValueInArray = (list, keyName, keyValue, propertyName, propertyValue) => {
  return list.map(el => (el[keyName] === keyValue ? Object.assign({}, el, { [propertyName]: propertyValue }) : el));
};

export const isEmpty = obj => {
  for (var key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) return false;
  }
  return true;
};

export const extractGraphQLErrorMessage = errorObj => {
  if (errorObj.graphQLErrors) {
    const errors = errorObj.graphQLErrors.map(x => x?.extensions?.exception?.message).filter(message => Boolean(message));
    if (errors.length > 0) return errors.join('\n');
  }
  if (errorObj.message) return errorObj.message;
  return errorObj;
};

export const extractAgeFromCnp = p_cnp => {
  if (p_cnp && isCnp(p_cnp)) {
    // Extract birthday
    const extraDateValues = '00:00:00 +0000';
    const sex = parseInt(p_cnp[0], 10);
    let year = parseInt(`${p_cnp[1]}${p_cnp[2]}`, 10);
    const month = parseInt(`${p_cnp[3]}${p_cnp[4]}`, 10);
    const day = parseInt(`${p_cnp[5]}${p_cnp[6]}`, 10);

    switch (sex) {
      case 1:
      case 2:
        year += 1900;
        break;
      case 3:
      case 4:
        year += 1800;
        break;
      case 5:
      case 6:
        year += 2000;
        break;
      case 7:
      case 8:
      case 9:
        year += 2000;
        if (year > new Date().getFullYear() - 18) {
          year -= 100;
        }
        break;
      default: {
        return undefined;
      }
    }

    if (year < 1800 || year > 2099) {
      return undefined;
    }

    const birthday = Date.parse(`${year}-${month}-${day} ${extraDateValues}`);

    // Extract age
    const diff_ms = Date.now() - birthday;
    const age_dt = new Date(diff_ms);
    return age_dt.getUTCFullYear() - 1970;
  }

  return undefined;
};

export const combineStyles = (...styles) => {
  return function CombineStyles(theme) {
    const outStyles = styles.map(arg => {
      // Apply the "theme" object for style functions.
      if (typeof arg === 'function') {
        return arg(theme);
      }
      // Objects need no change
      return arg;
    });

    return outStyles.reduce((acc, val) => Object.assign(acc, val));
  };
};

export const isJsonString = (value, errorMessage) => {
  try {
    JSON.parse(value);
  } catch (e) {
    window.confirm(errorMessage);
    return false;
  }
  return true;
};

export const getPlainStringFromJson = value => JSON.stringify(value).replace(/"\s+|\s+"/g, '"');

export const getDeepCopy = input => clone(input);

// valueOrDefault :: a -> a -> a
export const valueOrDefault = curry(($default, value) => {
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  return value || $default;
});

export const groupBy = (array, f) => {
  let groups = {};
  array.forEach(function (o) {
    var group = JSON.stringify(f(o));
    groups[group] = groups[group] || [];
    groups[group].push(o);
  });
  return Object.keys(groups).map(function (group) {
    return groups[group];
  });
};

export const getOidcConfigName = () => {
  const tid = 'config_show_access_token';
  return tid;
};

export function camelize(str) {
  if (!str) return '';
  return str.replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, function (match, index) {
    if (+match === 0) return ''; // or if (/\s+/.test(match)) for white spaces
    return index === 0 ? match.toLowerCase() : match.toUpperCase();
  });
}

const byPropertiesOf = (fields, direction) => (obj1, obj2) => {
  let sortOrder = direction === 'asc' ? 1 : -1;
  let result = 0;
  for (let i = 0; i < fields.length; i++) {
    const field = fields[i];
    if (obj1[field] < obj2[field]) {
      result = -1;
      break;
    }
    if (obj1[field] > obj2[field]) {
      result = 1;
      break;
    }
    // sort the rest ascending if the primary field is equal
    sortOrder = 1;
  }
  return result * sortOrder;
};

export const sortBy = (array, fields, direction = 'asc') => {
  array.sort(byPropertiesOf(fields, direction));
};

export const getDynamicFieldControlType = typeCode => {
  switch (typeCode) {
    case 'TP_TEXT':
      return ControlType.Text;
    case 'COMBO':
    case 'SEARCH':
    case 'SELECT':
    case 'SEARCH_BIT':
    case 'ADAPTIVE_LOOKUP':
      return ControlType.Autocomplete;
    case 'TP_NUM':
    case 'AMOUNT':
      return ControlType.Numeric;
    case 'TP_DATE':
    case 'DATE':
      return ControlType.Date;
    case 'BOOL':
      return ControlType.Checkbox;
    default:
      return ControlType.Text;
  }
};

export const isObject = value => typeof value === 'object' && !Array.isArray(value) && value !== null;

export const withPropsExclusion = props => {
  if (!props) return;
  if (Array.isArray(props) && props.length === 0) return;
  if (!Array.isArray(props) && typeof props !== 'string') return;

  return {
    shouldForwardProps: prop => {
      if (typeof props === 'string') return prop !== props;
      return !props.includes(prop);
    }
  };
};

export const onSimpleSelectChange = (lens, prefix) => value => {
  over(lens, prev => {
    const next = { ...prev };
    if (value.id) next[`${prefix}Id`] = value.id;
    if (value.code) next[`${prefix}Code`] = value.code;
    if (value.name) next[`${prefix}Name`] = value.name;
    if (value.uniqueId) next[`uniqueId`] = value.uniqueId;
    return next;
  });
};

export const parseId = value => (typeof value === 'string' ? null : value);

// to be used for entities with a number as id
export const isNewEntity = value => {
  if (!value || !value.id) return false;
  if (typeof value.id === 'string') return true;
  return false;
};

/** noOptionsText can receive a string/component but also a function; if a function was passed,
 * Autocomplete forwards the search input value and the loading status of the options to the options.
 * For loading, Autocomplete automatically handles it with a progress bar, so most likely the option is
 * not needed (but it's there). Using the first parameter (the search string), a different message can be
 * displayed instead of the default 'No options' - bear in mind the function can also return a React
 * component if needed.
 */
export const getAsyncAutocompleteNoOptionsText = (search, _loading) => {
  if (!search) return i18next.t('TypeToSearch');
  return i18next.t('NoOptions');
};
